import { IContentExtract, IMeasure } from "@/core/shared/api/types";
import { filterStateSchema, IGetFiltersByGroupResult } from "@/core/utils/legend-state/feature/filter";
import { z } from "zod";
import { IPeersetCompany } from "@/features/report-builder/state/types";

const config = z.object({
  company: z.object({
    measure_filter_to_measure_name_map: z.record(
      z.object({
        default: z.array(z.string()),
      }),
    ),
    filters: z.object({
      hide: z.boolean(),
    }),
  }),
  peer_set_avg: z.object({
    PEER_SET_AVG_MEAN: z.object({
      id: z.string(),
      label: z.string(),
    }),
    PEER_SET_AVG_MEDIAN: z.object({
      id: z.string(),
      label: z.string(),
    }),
  }),
  measure: z.object({
    hide: z.boolean(),
    default: z.string(),
    measures: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
        unit: z.string().optional(),
        acronym: z.string().optional(),
        showSign: z.boolean().optional(),
        domain: z.tuple([z.number(), z.number()]).optional(),
        compactYAxisValues: z.boolean().optional(),
        filter: z.object({
          checked: z.boolean(),
          disabled: z.boolean().optional(),
        }),
      }),
    ),
  }),
  benchmark: z.object({
    hide: z.boolean(),
    benchmarks: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
        measure_filter_to_measure_name_map: z.record(
          z.object({
            default: z.array(z.string()),
          }),
        ),
        filter: z.object({
          id: z.string().optional(),
          checked: z.boolean(),
          disabled: z.boolean(),
          label: z.string().optional(),
        }),
      }),
    ),
  }),
  visualisation: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
    }),
  ),
});

const axis_schema = z.object({
  data_key: z.string().optional(),
  label: z.string().optional(),
  compactValues: z.boolean().optional(),
  domain: z.tuple([z.number(), z.number()]).optional(),
  unit: z.string().optional(),
});

export const local_schema = z.object({
  label: z.string(),
  ui_merged: z.boolean(),
  config,
  views: z.object({
    currentView: z.union([z.literal("analysis"), z.literal("alerts")]),
    analysis: z.object({
      chart_config: z.object({
        entities: z.record(
          z.string(),
          z.array(
            z.object({
              id: z.string(),
              label: z.string(),
              color: z.string(),
            }),
          ),
        ),
        polar_axis: axis_schema,
      }),
      selectors: z.object({
        measure: z.object({
          selectedMeasureId: z.string(),
        }),
        peer_set_avg_type: z.object({
          selected: z.string(),
          applied: z.string(),
        }),
        peer_set_avg_mode: z.object({
          selected: z.boolean(),
          applied: z.boolean(),
        }),
        visualisation: z.object({
          selected: z.string(),
        }),
      }),
      filters: z.object({
        company: filterStateSchema,
        peer_set_avg_contributors: filterStateSchema,
        timeframe: filterStateSchema,
        benchmark: filterStateSchema,
        applied_filters: z.object({
          company_id: z.array(z.string()).optional(),
          timeframe: z.array(z.string()).optional(),
          benchmarks: z.array(z.string()).optional(),
          peer_set_avg_contributors: z.array(z.string()).optional(),
        }),
      }),
    }),
  }),
});

export type ILocalState$ = z.infer<typeof local_schema>;

export type IRemoteState$ = {
  measures: {
    pending: boolean;
    error: boolean;
    data: {
      eps_data: Array<IMeasure>;
      chart_data: Record<string, number | string | null>[];
      table_data: Record<
        string,
        {
          id: string;
          score: number | string | null;
          label: string;
          signal?: string;
        }
      >[];
    };
  };
};

export interface ICreateStateProps {
  local?: object;
}

export interface IActions {
  views: {
    analysis: {
      selectors: {
        measure: {
          set: (instance_id: string, measure_id: string) => void;
        };
        visualisation: {
          set: (instance_id: string, visualisation: string) => void;
        };
        peer_set_avg_type: {
          set: (instance_id: string, peer_set_avg_type: string) => void;
          apply: (instance_id: string) => void;
        };
        peer_set_avg_mode: {
          set: (instance_id: string, peer_set_avg_mode: boolean) => void;
          apply: (instance_id: string) => void;
        };
      };
      filters: {
        benchmark: {
          applyAll: (instance_id: string) => void;
          toggle: (instance_id: string, filter_id: string) => void;
          toggleGroup: (instance_id: string, filter_group: string) => void;
          getFiltersByGroup: (instance_id: string) => IGetFiltersByGroupResult;
        };
        peer_set_avg_contributors: {
          applyAll: (instance_id: string) => void;
          toggle: (instance_id: string, filter_id: string) => void;
          toggleGroup: (instance_id: string, filter_group: string) => void;
          getFiltersByGroup: (instance_id: string) => IGetFiltersByGroupResult;
        };
        company: {
          applyAll: (instance_id: string) => void;
          toggle: (instance_id: string, filter_id: string) => void;
          toggleGroup: (instance_id: string, filter_group: string) => void;
          getFiltersByGroup: (instance_id: string) => IGetFiltersByGroupResult;
        };
        timeframe: {
          applyAll: (instance_id: string) => void;
          toggle: (instance_id: string, filter_id: string) => void;
          toggleGroup: (instance_id: string, filter_group: string) => void;
          toggleByRange: (instance_id: string, range: `${number}-${number}`) => void;
          getFiltersByGroup: (instance_id: string) => IGetFiltersByGroupResult;
        };
      };
    };
  };
}

export interface IObservers {
  view: {
    analysis: {
      chart_config: {
        sync_to_state: (instance_id: string) => void;
      };
      filters: {
        benchmark: {
          init: (instance_id: string) => void;
        };
        peer_set_avg_contributors: {
          init: (instance_id: string) => void;
        };
        company: {
          init: (instance_id: string) => void;
        };
        timeframe: {
          init: (instance_id: string) => void;
        };
      };
    };
  };
}

export interface ITransformedContentExtract extends IContentExtract {
  company: IPeersetCompany | null;
}
